'use client';

import { FC, useState } from 'react';
import { toast } from '@/hotdesk-ui/components/toast-v4';
import { useSelector } from 'react-redux';
import { SendCode } from 'hotdesk_typescript';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { AuthService } from '@/hotdesk-ui/services/auth.service';
import { UserService } from '@/hotdesk-ui/services/user.service';
import { dispatch as storeDispatch, RootState } from '@/hotdesk-ui/store/store';
import { setUserData } from '@/hotdesk-ui/store/slices/auth.slice';
import AuthShell from './AuthShell';
import VerifyScreen from './VerifyScreen';
export interface VerifyPhoneModalV4Props {
  show: boolean;
  handleClose: () => void;
  /** ISO locale used to scope the auth/user services. */
  language: string;
  phoneNumber: string;
  userId?: number;
  /** Optional callback fired after a successful verification. */
  onVerified?: () => void;
  termLink?: string;
  privacyPolicyLink?: string;
}
const getAuthService = async (languageCode: string): Promise<AuthService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new AuthService(apiConfig);
};
const getUserService = async (languageCode: string): Promise<UserService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new UserService(apiConfig);
};

// Standalone v4 phone-verification modal. Use this for the post-login case
// where an action (booking submit, subscription purchase, etc.) requires a
// verified phone. The signup flow keeps using the verify step inside
// AuthModalV4 since it has shared state with the rest of that flow.
const VerifyPhoneModalV4: FC<VerifyPhoneModalV4Props> = ({
  show,
  handleClose,
  language,
  phoneNumber,
  userId,
  onVerified,
  termLink,
  privacyPolicyLink
}) => {
  const reduxUser = useSelector((state: RootState) => state.auth.user);
  const [isLoading, setIsLoading] = useState(false);
  const [isInvalid, setIsInvalid] = useState(false);
  const close = () => {
    setIsInvalid(false);
    setIsLoading(false);
    handleClose();
  };
  const onVerify = async (otp: string) => {
    if (!userId) return;
    setIsLoading(true);
    setIsInvalid(false);
    try {
      const authService = await getAuthService(language);
      const userService = await getUserService(language);
      const response = await authService.varifyPhoneNumber(userId, {
        pinCode: otp
      });
      if (response) {
        toast.success('Phone number verified successfully.', {
          position: 'bottom-center'
        });
      }
      if (reduxUser) {
        const updated = await userService.updateUser(reduxUser.id ?? 0, {
          firstName: reduxUser.firstName,
          lastName: reduxUser.lastName,
          email: reduxUser.email,
          username: reduxUser.username,
          isPhoneVerified: true
        });
        if (updated) storeDispatch(setUserData(updated));
      }
      await firebaseAnalytics('web_otp_accepted');
      onVerified?.();
      close();
    } catch (error: any) {
      await firebaseAnalytics('web_otp_error', {
        error: JSON.stringify(error)
      });
      const errorMessage = error?.body?.detail || error?.response?.data?.detail || error?.data?.detail || error?.message || 'Verification phone failed. Please try again.';
      toast.error(errorMessage, {
        position: 'bottom-center'
      });
      setIsInvalid(true);
    } finally {
      setIsLoading(false);
    }
  };
  const onResend = async () => {
    try {
      await firebaseAnalytics('web_otp_request_resent');
      const authService = await getAuthService(language);
      const sendCode: SendCode = {
        phoneNumber: phoneNumber.replace(/\s+/g, ''),
        username: reduxUser?.username
      } as SendCode;
      if (sendCode.username && sendCode.phoneNumber) {
        await authService.sendPhoneCode(sendCode);
      }
      setIsInvalid(false);
    } catch (error: any) {
      const message = error?.message || error?.body?.detail || 'Unable to resend';
      toast.error(message, {
        position: 'bottom-center'
      });
    }
  };
  return <AuthShell show={show} onClose={close} data-sentry-element="AuthShell" data-sentry-component="VerifyPhoneModalV4" data-sentry-source-file="VerifyPhoneModalV4.tsx">
            <VerifyScreen phoneNumber={phoneNumber} isLoading={isLoading} isInvalid={isInvalid} onVerify={onVerify} onResend={onResend} onSkip={close} termLink={termLink} privacyPolicyLink={privacyPolicyLink} data-sentry-element="VerifyScreen" data-sentry-source-file="VerifyPhoneModalV4.tsx" />
        </AuthShell>;
};
export default VerifyPhoneModalV4;